import helpers from "utils/helpers";
import moment from "moment";

const requisitionCreateInputsByHash = {
  collaborator: {
    label: "Collaborator",
    yup: helpers.getYupRequired("Collaborator"),
    type: "autocomplete",
    relativeInputs: ["clientId"],
    resetRelativeInputs: ["clientId", "orderingClientPhysId"],
    resource: "collaborators",
    sort: {
      field: "colaName",
      order: "asc",
    },
    accessor: (d) => {
      return { value: d.id, label: d.colaName, extraSelectedOptionData: { colaName: d.colaName } };
    },
    setInitValueFromData: {
      inputKey: "colaAlias",
      outputKey: "id",
      value: "MSL",
    },
    relativeHideVisibilityInputs: ["clientOrderId"],
  },
  clientId: {
    label: "Client",
    yup: helpers.getYupRequired("Client"),
    type: "autocomplete",
    relativeInputs: ["orderingClientPhysId"],
    resetRelativeInputs: ["orderingClientPhysId"],
    resource: "clients",
    sort: {
      field: "clientName",
      order: "asc",
    },
    relativeInputsFilterAccessor: (d) => {
      return { colaId: d.collaborator };
    },
    accessor: (d) => {
      return {
        value: d.id,
        label: `${d.legacyClientId} - ${d.clientName}`,
        extraSelectedOptionData: { id: d.id, clientTz: d.clientTz, clientName: d.clientName },
      };
    },
  },
  orderingClientPhysId: {
    label: "Physician",
    yup: helpers.getYupRequired("Physician"),
    type: "autocomplete",
    relativeInputResourceAccessor: (d) => {
      return `clients/${d.clientId}/physicians`;
    },
    accessor: (d) => {
      return { value: d.id, label: `${d.physFirstName} ${d.physLastName}` };
    },
    sort: {
      field: "physLastName",
      order: "asc",
    },
  },
  isProviderSignature: {
    label: "Provider Signature",
    yup: helpers.getYupRequired("Provider Signature"),
    type: "select",
    options: [
      { value: 1, label: "Yes", shortKeyCode: "y" },
      { value: 0, label: "No", shortKeyCode: "n" },
    ],
  },
  clientOrderId: {
    label: "External Order Id",
    selectOnFocus: true,
    hideVisibilityAccessor: (val, extraData, input) => {
      // Very robust check (ID, Alias, or Name variations)
      const extra = input?.extraSelectedOptionData || {};
      const name = (extra.colaName || "").toLowerCase();
      const alias = (extra.colaAlias || "").toLowerCase();
      return (
        String(val) === "36" ||
        alias === "msl" ||
        name.includes("morning star") ||
        name.includes("morningstar")
      );
    },
  },
  patientDob: {
    label: "Date of birth",
    placeholder: "MM / DD / YYYY",
    yup: helpers.getYupRequired("Date of birth"),
    selectOnFocus: true,
    required: false,
    inputWrapStyles: "width: auto; min-width: 250px; margin-top: 10px;",
  },

  patientFirstName: {
    label: "Patient First Name",
    yup: helpers.getYupCapitalizedRequired("Patient First Name"),
    submitValueAccessor: (d) => helpers.capitalizeName(d),
    selectOnFocus: true,
    inputWrapStyles: "width: auto; min-width: 120px;",
  },
  patientMiddleName: {
    label: "Patient Middle Name",
    yup: helpers.getYupMiddleNameOptional("Middle Name"),
    submitValueAccessor: (d) => helpers.capitalizeName(d),
    required: false,
    selectOnFocus: true,
    inputWrapStyles: "width: auto; min-width: 150px;",
  },
  patientLastName: {
    label: "Patient Last Name",
    yup: helpers.getYupCapitalizedRequired("Patient Last Name"),
    submitValueAccessor: (d) => helpers.capitalizeName(d),
    selectOnFocus: true,
    inputWrapStyles: "width: auto; min-width: 120px;",
  },
  patientGenderCode: {
    label: "Sex",
    type: "select",
    options: [
      { value: "MALE", label: "Male", shortKeyCode: "m" },
      { value: "FEMALE", label: "Female", shortKeyCode: "f" },
      { value: "UNKNOWN", label: "Unknown", shortKeyCode: "u" },
    ],
    inputWrapStyles: "width: 120px; margin-top: 8px;",
  },

  dateCollected: {
    label: "Date Collected",
    placeholder: "MM / DD / YYYY",
    yup: helpers.getYupRequired("Date Collected"),
    selectOnFocus: true,
    inputWrapStyles: "width: auto; min-width: 180px; margin-top: 10px;",
  },
  timeCollected: {
    label: "Time Collected",
    placeholder: "HH : MM",
    yup: helpers.getYupOptional("Collected Time"),
    selectOnFocus: true,
    inputWrapStyles: "width: auto; min-width: 140px; margin-top: 10px;",
  },
  timeCollectedAmPm: {
    label: "AM/PM",
    yup: helpers.getYupOptional("Time of day"),
    type: "select",
    options: [
      { value: "am", label: "AM", shortKeyCode: "a" },
      { value: "pm", label: "PM", shortKeyCode: "p" },
    ],
    value: "am",
    inputWrapStyles: "max-width: 90px; margin-top: 10px;",
    required: false,
  },
  isDiabetic: {
    label: "Diabetes",
    type: "select",
    options: [
      { value: 1, label: "Yes", shortKeyCode: "y" },
      { value: 0, label: "No", shortKeyCode: "n" },
    ],
    value: 0,
    inputWrapStyles: "max-width: 160px; margin-top: 8px;",
  },
  isFamilyHistory: {
    label: "Family History of MI",
    type: "select",
    options: [
      { value: 1, label: "Yes", shortKeyCode: "y" },
      { value: 0, label: "No", shortKeyCode: "n" },
    ],
    value: 0,
    inputWrapStyles: "max-width: 160px; margin-top: 8px;",
  },
  testOrderedCodes: {
    label: "Test Ordered",
    type: "autocomplete",
    value: [],
    resource: "lovs/TESTORDERED",
    subType: "multi",
    sort: {
      field: "testOrderedDesc",
      order: "asc",
    },
    accessor: (d) => ({ value: d.testOrderedCode, label: d.testOrderedDesc }),
    yup: helpers.getYupArrayRequired("Test Ordered"),
    autocompleteOptions: {
      loadDataAfterOpenMenu: true,
    },
    setInitValueFromData: {
      inputKey: "testOrderedCodes",
      outputKey: "testOrderedCodes",
    },
    inputWrapStyles: "min-width: 160px; max-width: 40%;",
  },

  specContainerTypeCode: {
    label: "Specimen / Container Type",
    type: "autocomplete",
    value: "SERUM-SST",
    resource: "lovs/SPECCONTAINERTYPE",
    accessor: (d) => ({ value: d.specContainerTypeCode, label: d.specContainerTypeDesc }),
    yup: helpers.getYupRequired("Specimen / Container Type"),
    excludeSubmit: false,
    inputWrapStyles: "max-width: 160px;",
    setInitValueFromData: {
      inputKey: "specContainerTypeCode",
      outputKey: "specContainerTypeCode",
      value: "SERUM-SST",
    },
  },
  specimenConditionCode: {
    label: "Temperature Type",
    type: "autocomplete",
    resource: "lovs/TEMPERATURETYPE",
    accessor: (d) => ({
      value: d.temperatureTypeCode,
      label: d.temperatureTypeDesc,
    }),
    yup: helpers.getYupRequired("Temperature Type"),
    setInitValueFromData: {
      inputKey: "temperatureTypeDesc",
      outputKey: "temperatureTypeCode",
      value: "Refrigerated",
    },
    inputWrapStyles: "max-width: 160px;",
  },
  tubeCount: {
    label: "Number of Tube(s)",
    type: "number",
    inputProps: { min: "0" },
    value: 1,
    yup: helpers.getYupRequired("Number of Tube(s)"),
    inputWrapStyles: "max-width: 160px;",
    required: false,
    selectOnFocus: true,
  },
  panelIds: {
    label: "Panel Tests",
    type: "resourceMultiSelect",
    resourceAccessor: (d) => {
      return `requisitions/${d.reqID}/panels`;
    },
    accessor: (d) => {
      return { value: d.id, label: `${d.panelCode} - ${d.panelDesc}` };
    },
    excludeSubmit: true,
    inputWrapStyles: "max-width: 300px;",
  },
  testIds: {
    label: "A La Carte Tests",
    type: "resourceMultiSelect",
    resourceAccessor: (d) => {
      return `requisitions/${d.reqID}/single_tests`;
    },
    accessor: (d) => {
      return { value: d.id, label: `${d.testCode} - ${d.testDesc}` };
    },
    excludeSubmit: true,
    inputWrapStyles: "max-width: 300px;",
  },
  dateReceived: {
    label: "Date Received",
    placeholder: "MM / DD / YYYY",
    yup: helpers.getYupRequired("Received Date"),
    value: moment(new Date()).format("MM / DD / YYYY"),
    inputWrapStyles: "width: auto; min-width: 180px; margin-top: 10px;",
    selectOnFocus: true,
  },
  timeReceived: {
    label: "Time Received",
    placeholder: "HH : MM",
    yup: helpers.getYupRequired("Received Time"),
    value: moment(new Date()).format("hh : mm"),
    inputWrapStyles: "width: auto; min-width: 140px; margin-top: 10px;",
    selectOnFocus: true,
  },
  dateReceivedAmPm: {
    label: "AM/PM",
    yup: helpers.getYupRequired("Received AM/PM"),
    type: "select",
    options: [
      { value: "am", label: "AM", shortKeyCode: "a" },
      { value: "pm", label: "PM", shortKeyCode: "p" },
    ],
    value: moment(new Date()).format("a"),
    inputWrapStyles: "max-width: 90px; margin-top: 10px;",
  },
  reqBillTypeCode: {
    label: "Bill To",
    type: "autocomplete",
    resource: "lovs/REQBILLTYPE",
    accessor: (d) => {
      if (['THIRD_PARTY'].includes(d.reqBillTypeCode)) {
        return null;
      }
      return {
        value: d.reqBillTypeCode,
        label: d.reqBillTypeDesc,
      };
    },
    sort: {
      field: "reqBillTypeDesc",
      order: "asc",
    },
    yup: helpers.getYupRequired("Bill To"),
    inputWrapStyles: "max-width: 160px;",
    relativeHideVisibilityInputs: [
      "isInsCardProvided",
      "isSelfPayProvided",
      "isIcdProvided",
      "patientEmail",
      "guarantorAddr1",
      "guarantorAddr2",
      "guarantorCity",
      "guarantorStateId",
      "guarantorCountryId",
      "guarantorZip",
    ],
    relativeInputs: [
      "isIcdProvided",
    ],
  },
  isInsCardProvided: {
    label: "Insurance Provided?",
    type: "select",
    options: [
      { value: 1, label: "Yes", shortKeyCode: "y" },
      { value: 0, label: "No", shortKeyCode: "n" },
    ],
    required: true,
    yup: helpers.getYupRequired("Insurance Provided"),
    inputWrapStyles: "max-width: 220px;",
    hideVisibilityAccessor: (val) => {
      return val !== "INS" && val !== "INSURANCE";
    },
  },
  isSelfPayProvided: {
    label: "Self Payment Provided?",
    type: "select",
    options: [
      { value: 1, label: "Yes", shortKeyCode: "y" },
      { value: 0, label: "No", shortKeyCode: "n" },
    ],
    required: true,
    yup: helpers.getYupRequired("Self Payment Provided").when("reqBillTypeCode", {
      is: "SELF",
      then: (schema) => schema.required("Self Payment Provided is required"),
      otherwise: (schema) => schema.optional().nullable(),
    }),
    inputWrapStyles: "max-width: 220px;",
    hideVisibilityAccessor: (val) => {
      return val !== "SELF" && val !== "TESTING";
    },
  },
  isIcdProvided: {
    label: "ICD Codes Provided?",
    type: "select",
    options: [
      { value: 1, label: "Yes", shortKeyCode: "y" },
      { value: 0, label: "No", shortKeyCode: "n" },
    ],
    requiredAccessor: (d) => {
      const val = d.reqBillTypeCode;
      return val === "INS" || val === "INSURANCE";
    },
    yup: helpers.getYupRequired("ICD Codes Provided").when("reqBillTypeCode", {
      is: (val) => val === "INS" || val === "INSURANCE",
      then: (schema) => schema.required("ICD Codes Provided is required"),
      otherwise: (schema) => schema.optional().nullable(),
    }),
    inputWrapStyles: "max-width: 180px;",
  },
  patientEmail: {
    label: "Patient Email",
    inputWrapStyles: "max-width: 200px;",
    selectOnFocus: true,
    required: false,
    hideVisibilityAccessor: (val) => {
      return val === "TRAIN";
    },
  },
  guarantorAddr1: {
    label: "Street Number and Name",
    required: false,
    inputWrapStyles: "width: auto; min-width: 120px;",
    selectOnFocus: true,
    hideVisibilityAccessor: (val) => {
      return val !== "INS" && val !== "INSURANCE" && val !== "SELF";
    },
  },
  guarantorAddr2: {
    label: "Unit/Floor Number",
    required: false,
    inputWrapStyles: "width: auto; min-width: 100px;",
    selectOnFocus: true,
    hideVisibilityAccessor: (val) => {
      return val !== "INS" && val !== "INSURANCE" && val !== "SELF";
    },
  },
  guarantorCity: {
    label: "City",
    required: false,
    inputWrapStyles: "width: auto; min-width: 100px;",
    selectOnFocus: true,
    hideVisibilityAccessor: (val) => {
      return val !== "INS" && val !== "INSURANCE" && val !== "SELF";
    },
  },
  guarantorCountryId: {
    label: "Country",
    type: "autocomplete",
    resource: "countries",
    required: false,
    relativeInputs: ["guarantorStateId"],
    resetRelativeInputs: ["guarantorStateId"],
    accessor: (d) => ({ value: d.id, label: d.countryLongName }),
    inputWrapStyles: "width: auto; min-width: 150px;",
    hideVisibilityAccessor: (val) => {
      return val !== "INS" && val !== "INSURANCE" && val !== "SELF";
    },
  },
  guarantorStateId: {
    label: "State",
    type: "autocomplete",
    resource: "states",
    relativeInputsFilterAccessor: (d) => {
      return { countryId: d.guarantorCountryId };
    },
    accessor: (d) => ({ value: d.id, label: d.stateLongname }),
    required: false,
    inputWrapStyles: "max-width: 160px;",
    hideVisibilityAccessor: (val) => {
      return val !== "INS" && val !== "INSURANCE" && val !== "SELF";
    },
  },
  guarantorZip: {
    label: "Zip",
    required: false,
    inputWrapStyles: "width: auto; min-width: 80px;",
    selectOnFocus: true,
    hideVisibilityAccessor: (val) => {
      return val !== "INS" && val !== "INSURANCE" && val !== "SELF";
    },
  },
  failIds: {
    label: "Manually Entered Issues",
    type: "autocomplete",
    resource: "reasons/FAIL",
    subType: "multi",
    accessor: (d) => ({
      value: d.id,
      label: `${d.failCode} - ${d.failDesc}`,
    }),
    sort: {
      field: "failCode",
      order: "asc",
    },
    value: [],
    excludeSubmit: true,
    required: false,
    inputWrapStyles: "max-width: none;",
  },
};


export default requisitionCreateInputsByHash;
